import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import TextField from '@mui/material/TextField'
import { useSearchData } from 'Components/General/SearchProvider/SearchProvider'
import DataContext from 'Components/reusable/DataContext'
import { InputField } from 'Components/reusable/InputFields/InputField'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { chooseDBTranslation } from 'utils/translations'
import { fuseFiltering } from 'utils/utils'
import { useUser } from 'hooks'
import { IIndustry, IRole, ISkill } from 'types/levelItemInterfaces'
import { ICertificate, ICompetence } from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { combinedSearchApi } from 'api/CombinedSearchAPI'
import SearchFields from '../SearchComponents/SearchFields'
import TermsDisplay from '../TermsDisplay'
import { convertToTokens } from '../tokenUtils'
import { mapCombinedCandidateToSearchResult } from '../combinedSearchMapping'
import { sortByMatchQuality } from '../Results/matchQuality'

/** @notExported */
interface ICombinedSearchProps {
  /** Whether a search is currently in progress. */
  loading: boolean
  /** Setter for the loading state - the parent page renders the shared loading indicator/results view. */
  setLoading: (loading: boolean) => void
  /** The skills that can be selected. */
  skillOptions: ISkill[]
  /** The roles that can be selected. */
  roleOptions: IRole[]
  /** The industries that can be selected. */
  industryOptions: IIndustry[]
  /** The certificates that can be selected. */
  certificateOptions: ICertificate[]
  /** The competences that can be selected. */
  competenceOptions: ICompetence[]
}

/**
 * Combined search: structured search fields plus an optional free text field, submitted together
 * to the combined search service in one request. Uses the shared `SearchProvider` context/`Results`
 * view, so results render with the same match-quality chips and free-text detail box the free-text
 * search results show (`ResultCard`/`FreeTextMatchInfo`). `Results/index.tsx` hydrates pages through
 * the combined service's `search/results` endpoint.
 *
 * @return {React.ReactElement} The combined search component.
 */
const CombinedSearch: React.FC<ICombinedSearchProps> = ({
  loading,
  setLoading,
  skillOptions,
  roleOptions,
  industryOptions,
  certificateOptions,
  competenceOptions,
}) => {
  const { t, i18n } = useTranslation()
  const { searchData, setSearchData, searchableNetworks } = useSearchData()
  const { groups } = useUser()
  const [backendError, setBackendError] = useState<IError>()

  const { networks } = searchData.terms

  const onSubmit = async () => {
    const { terms } = searchData

    try {
      setLoading(true)
      const tokens = convertToTokens(terms)
      const networkIds = terms.networks.map(network => network.id)
      const freeText = 'query' in terms ? terms.query : undefined

      const response = await combinedSearchApi.search({
        networkIds,
        tokens,
        partial: true,
        freeText: freeText && freeText.trim().length > 0 ? freeText : undefined,
      })

      setSearchData({
        terms,
        results: sortByMatchQuality(response.candidates.map(mapCombinedCandidateToSearchResult)),
        improvedQuery: response.improvedQuery,
        lastFetched: new Date(),
        ownOrganization: terms.networks.length === 1 && terms.networks[0].autoGenerated,
      })
    } catch (err) {
      setBackendError(err as IError)
    } finally {
      setLoading(false)
    }
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <DataContext
      data={searchData.terms}
      onChange={values => setSearchData({ ...searchData, terms: { ...values } })}
      onSubmit={onSubmit}
      localeBase="search.combinedSearch"
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <Grid container spacing={1} alignItems="center">
            <Grid sx={{ fontWeight: 'bold' }}>{t('search.from')}</Grid>
            <Grid>
              <Autocomplete
                data-testid="autocomplete-combined-search-networks"
                noOptionsText={t('controls.noOptionsText')}
                options={searchableNetworks ?? []}
                getOptionLabel={option => chooseDBTranslation(i18n, option).name}
                value={networks}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_e, value) => {
                  if (!value) return
                  setSearchData({ ...searchData, terms: { ...searchData.terms, networks: value } })
                }}
                renderInput={params => (
                  <TextField
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { ...params?.inputProps, 'aria-label': t('search.from') },
                      input: params?.InputProps,
                    }}
                    {...params}
                    sx={{ minWidth: 200 }}
                    name="networks"
                    size="small"
                    variant="standard"
                    margin="dense"
                  />
                )}
                filterOptions={(options, { inputValue }) => fuseFiltering(options, inputValue, ['translations.name'])}
                getOptionDisabled={option => !!networks.find(item => item.id === option.id)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      sx={{ mr: 0.5, borderRadius: 0.5 }}
                      variant="outlined"
                      size="small"
                      label={chooseDBTranslation(i18n, option).name}
                    />
                  ))
                }
                multiple
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        <SearchFields
          skillOptions={skillOptions}
          roleOptions={roleOptions}
          industryOptions={industryOptions}
          certificateOptions={certificateOptions}
          competenceOptions={competenceOptions}
        />
        {!groups.includes('freelancer') && (
          <Grid size={12}>
            <InputField
              value={'query' in searchData.terms ? searchData.terms.query : ''}
              label={t('search.freeText')}
              helperText={t('search.freeTextHelper')}
              name="freeText"
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSearchData({
                  ...searchData,
                  terms: {
                    ...searchData.terms,
                    query: event.target.value,
                  },
                })
              }}
            />
          </Grid>
        )}
        <TermsDisplay />
        <Grid size={12}>
          <Button
            data-testid="button-combined-search-submit"
            variant="contained"
            onClick={onSubmit}
            disabled={!(!loading && searchData.terms.networks.length > 0)}
            color="newPrimary"
          >
            {t('resources.searchEmployees.search')}
          </Button>
        </Grid>
      </Grid>
    </DataContext>
  )
}

export default CombinedSearch
